import { gql as apolloGql } from '@apollo/client';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

// Hand-typed rather than pulled from generated/graphql -- narrower than the full generated
// `SysUserWhere` (which exposes every scalar/relation filter on the model), matching only what
// the Users list's Brand/Role/Status filters actually build (see users-list-utils.ts).
export type SysUserListWhere = {
  activeFlag?: { equals: boolean };
  brandUser?: { some: { brandId: { in: string[] }; activeFlag: { equals: boolean } } };
  userGroupRel?: { some: { groupId: { in: string[] } } };
  stateUser?: { some: { stateId: { in: string[] }; activeFlag: { equals: boolean } } };
};

export type SysUserSortDirection = 'Asc' | 'Desc';

// Verified against SORTABLE_FIELDS.SysUser in apps/api/src/graphql/filters/sortable-fields.ts.
export type SysUserOrderByInput =
  | { firstName: SysUserSortDirection }
  | { lastName: SysUserSortDirection }
  | { email: SysUserSortDirection }
  | { phone: SysUserSortDirection }
  | { activeFlag: SysUserSortDirection };

export type GetUsersQueryVariables = {
  first?: number;
  after?: string | null;
  last?: number;
  before?: string | null;
  search?: string;
  where?: SysUserListWhere;
  orderBy?: SysUserOrderByInput[];
};

export type UsersListRow = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  activeFlag: boolean;
  roleNames: string[];
  stateCodes: string[];
};

export type GetUsersQueryResult = {
  sysUsers: {
    edges: Array<{ node: UsersListRow }>;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor: string | null;
      endCursor: string | null;
    };
    totalCount?: number;
  };
};

// roleNames, not userGroupRel, which is gated on userGroup:view and a FORBIDDEN nulls every node.
export const GET_USERS: TypedDocumentNode<GetUsersQueryResult, GetUsersQueryVariables> = apolloGql`
  query GetUsers(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $search: String
    $where: SysUserWhere
    $orderBy: [SysUserOrderBy!]
  ) {
    sysUsers(
      first: $first
      after: $after
      last: $last
      before: $before
      search: $search
      where: $where
      orderBy: $orderBy
    ) {
      edges {
        node {
          id
          firstName
          lastName
          email
          phone
          activeFlag
          roleNames
          stateCodes
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export type SysUserDetail = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  initials: string | null;
  activeFlag: boolean;
  departmentId: string | null;
  roleNames: string[];
};

export type GetSysUserQueryVariables = { id: string };
export type GetSysUserQueryResult = { sysUser: SysUserDetail | null };

// Hand-written `apolloGql` + hand-written result types here — codegen *can* parse this query,
// but its generated `GetSysUserQuery.sysUser.activeFlag` is `boolean | null` while callers
// depend on `SysUserDetail.activeFlag: boolean`. A typed `gql()` document can't be narrowed
// without breaking the overload match, so we keep explicit types on `apolloGql`.
//
// `departmentId`/`roleNames` are their own fields (see
// apps/api/src/graphql/types/sys-user/department-id-field.ts and role-names-field.ts), gated
// on `user:view` — deliberately NOT selecting `department { id }` or
// `userGroupRel { userGroup { ... } } }` (the relations exposed by generated.ts), which are
// gated on `department:view`/`userGroup:view` respectively and would 403 this whole query (and
// every tab on this page, not just Access) for any role with `user:view` but not those other
// permissions — an entirely ordinary combination. The Access tab fetches the user's *current
// role ids* itself, separately, via GET_USER_ROLE_IDS (sys-user-access.ts) — that part
// legitimately needs `userGroup:view` since it also needs GET_ROLES (same gate) to render the
// pickable options.
export const GET_SYS_USER: TypedDocumentNode<GetSysUserQueryResult, GetSysUserQueryVariables> =
  apolloGql`
  query GetSysUser($id: ID!) {
    sysUser(id: $id) {
      id
      firstName
      lastName
      email
      phone
      initials
      activeFlag
      departmentId
      roleNames
    }
  }
`;
